import { apiClient } from '@/api/api'
import { ChainFailedError, flattenSearchGroups, unwrapChainEnvelope } from '@/api/utils/chainEnvelope'
import { resolveChain } from '@/features/search/api/chainResolver'
import type {
  DefineFiltersResponse,
  RecentSearch,
  SearchAutocomplete,
  SearchHistoryResponce,
  ShoppingListResponse,
  shoppingListParams,
  shoppingListPayload
} from '@/types/searchItemsTypes'
import type { AxiosResponse } from 'axios'

export const setFilters = async (searchText: string) => {
  return apiClient.post<DefineFiltersResponse>('/shopping-list/define-filters', {
    search_text: searchText
  })
}

export interface ShoppingListOptions {
  /** Continues a previous page's chain so the backend slices its cached candidates. */
  chainId?: string | null
  signal?: AbortSignal
}

/**
 * The chain envelope is unwrapped here and nowhere else — callers keep receiving
 * the flat result shape they always have. A response that is still running is
 * awaited to a terminal state before it is handed back.
 */
export const shoppingList = async (
  payload: shoppingListPayload,
  params: shoppingListParams,
  options: ShoppingListOptions = {}
): Promise<AxiosResponse<ShoppingListResponse>> => {
  const response = await apiClient.post('/shopping-list', payload, {
    params: options.chainId ? { ...params, chain_id: options.chainId } : params,
    signal: options.signal
  })

  const chain = unwrapChainEnvelope<ShoppingListResponse>(response.data)

  if (chain.outcome === 'failed') {
    throw new ChainFailedError(chain.error ?? 'Search failed', chain.chainId)
  }

  if (chain.outcome === 'success' && chain.result) {
    return { ...response, data: flattenSearchGroups(chain.result) }
  }

  // Still running: the backend answers later, via SSE push or the status endpoint.
  if (!chain.chainId) {
    throw new ChainFailedError('Search is pending but no chain_id was returned', null)
  }

  const result = await resolveChain(chain.chainId, options.signal)
  return {
    ...response,
    data: flattenSearchGroups({ ...result, chain_id: result.chain_id ?? chain.chainId })
  }
}

export const searchHistory = async (
  size: number,
  searchType: string
): Promise<AxiosResponse<SearchHistoryResponce>> => {
  return apiClient.get('/search-history/get_history', {
    params: {
      search_type: searchType,
      size
    }
  })
}

export const deleteSearchHistory = async (ids: Array<RecentSearch['id']>) => {
  return apiClient.delete('/search-history', {
    data: ids
  })
}


export const uploadImage = async (image: File | Blob) => {
  // Use the typed upload endpoint: `uploaded_from=shopping-list` makes the API
  // flatten the RGBA PNG the browser produces to RGB JPEG before S3. The image
  // detection model returns no matches for alpha-channel images, so the legacy
  // /shopping-list/upload_img route (which stored the PNG verbatim) yielded
  // "0 products found". Query params are not case-transformed by the interceptor.
  return apiClient.postForm(
    '/images/upload_img',
    { img: image },
    { params: { uploaded_from: 'shopping-list' } }
  )
}

interface AutocompleteParams {
  search_text: string
  size?: number
  suggestions_size?: number
  suggested_brands_size?: number
}

/**
 * Suggestions are an enhancement, never a blocker: any failure or malformed
 * payload degrades to "no suggestions" so typing and search keep working.
 */
async function softDegrade<T>(
  request: () => Promise<AxiosResponse<T>>,
  fallback: T,
  label: string
): Promise<AxiosResponse<T>> {
  try {
    const response = await request()
    return response.data ? response : { ...response, data: fallback }
  } catch (error) {
    console.warn(`${label} unavailable, continuing without suggestions:`, error)
    return {
      data: fallback,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: {} as AxiosResponse<T>['config']
    }
  }
}

export const autocomplete = async (
  params: AutocompleteParams
): Promise<AxiosResponse<SearchAutocomplete>> => {
  return softDegrade<SearchAutocomplete>(
    () => apiClient.get('/shopping-list/autocomplete', { params }),
    { suggestions: [], suggested_brands: [] },
    'Autocomplete'
  )
}

export const similarRequests = async (searchText: string, size: number) => {
  return softDegrade<{ suggestions: string[] }>(
    () =>
      apiClient.get('/shopping-list/similar-requests', {
        params: {
          search_text: searchText,
          size
        }
      }),
    { suggestions: [] },
    'Similar requests'
  )
}
